import {
  buttonsFor,
  setAdded,
  setAdding,
  setFailed,
} from "../add-button/add-button";
import { cartState } from "./cart-state";
import { isDrawerOpen, openDrawer } from "./cart-drawer";

/**
 * Ajax add-to-cart for the product form and the collection quick-add.
 *
 * Replaces the jQuery handlers that used to live in `assets/theme.js`
 * (`theme.applyAjaxToProductForm` and the `.quick-add-to-cart` click binding).
 * Those refreshed the header by GET-ing the entire `/cart` page and scraping it;
 * here the add response carries the rendered drawer, so a single request covers
 * the cart write, the drawer and the bubble.
 *
 * Everything is delegated from `document`, so product forms injected later by
 * the section rendering in `theme.js` are picked up without re-binding.
 */

const PRODUCT_FORM = 'form[action*="/cart/add"]';
const QUICK_ADD =
  ".quick-add-to-cart [data-variant-id], " +
  ".quick-add-to-cart_size .product-block--option .avail--true[data-variant-id]";

function showFormError(form: HTMLFormElement, message: string): void {
  const container = form.querySelector<HTMLElement>(".product-status-message");
  if (!container) {
    window.alert(message);
    return;
  }

  container.textContent = message;
  container.setAttribute("role", "alert");
  container.style.display = "";
  window.setTimeout(() => {
    container.style.display = "none";
  }, 8000);
}

async function submitProductForm(form: HTMLFormElement): Promise<void> {
  const buttons = buttonsFor(form);
  buttons.forEach(setAdding);

  const formData = new FormData(form);
  const quantity = Number(formData.get("quantity") ?? 1) || 1;

  cartState.applyOptimisticDelta(quantity);
  const result = await cartState.add(formData);

  if (!result) {
    cartState.applyOptimisticDelta(-quantity);
    buttons.forEach(setFailed);
    return;
  }

  buttons.forEach(setAdded);
  openDrawer();
}

async function quickAdd(trigger: HTMLElement): Promise<void> {
  const variantId = Number(trigger.dataset.variantId);
  if (!variantId) return;

  // Close the size popup the way the previous implementation did.
  trigger
    .closest(".quick-add-to-cart")
    ?.querySelectorAll<HTMLElement>(".popup-option")
    .forEach((popup) => {
      popup.style.display = "none";
    });

  const container = document.querySelector<HTMLElement>(
    ".quick-add-to-cart-container",
  );
  if (container) container.style.bottom = "-250px";

  cartState.applyOptimisticDelta(1);
  const result = await cartState.add({ id: variantId, quantity: 1 });

  if (!result) {
    cartState.applyOptimisticDelta(-1);
    return;
  }

  openDrawer();
}

export function setupCartAddForms(): void {
  document.addEventListener(
    "submit",
    (event) => {
      const form = (event.target as Element | null)?.closest?.(PRODUCT_FORM);
      if (!(form instanceof HTMLFormElement)) return;

      // Honour the theme setting that sends customers straight to the cart page.
      if (form.classList.contains("feedback-go_to_cart")) return;
      if (!form.closest('[data-ajax-add-to-cart="true"]')) return;

      event.preventDefault();
      void submitProductForm(form);
    },
    // Bubble, not capture: `theme.js` binds `submit.variantSelectionGuard` on
    // the form itself (`bindRequiredSelections`) and stops propagation when a
    // required option is unselected. Listening from capture would add to the
    // cart before that guard ever ran.
    false,
  );

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest<HTMLElement>(QUICK_ADD);
    if (!trigger) return;

    event.preventDefault();
    void quickAdd(trigger);
  });

  document.addEventListener("cart:error", (event) => {
    const { message, key } = (
      event as CustomEvent<{ message: string; key?: string }>
    ).detail;

    // Line-scoped failures (e.g. a stock cap found when the edits were written)
    // belong on that cart line, not on the product form.
    if (key) return;

    const form = document.querySelector<HTMLFormElement>(
      '[data-ajax-add-to-cart="true"] form[action*="/cart/add"]',
    );
    if (form && !isDrawerOpen()) showFormError(form, message);
  });
}
